import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import HashLinkObserver from "react-hash-link"
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import TextField from '@mui/material/TextField'
import {SxProps, useMediaQuery, useTheme} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown'
import MoveToDetailIcon from '@mui/icons-material/ArrowRight'
import { PageHeader, Label, DisplayValue, ErrorInformation, BackToPageTop } from '../components'
import useContactCargo, { IRequest } from '../hooks/useContactCargo'
import useMcdApiErrorData from '../hooks/useMcdApiErrorData'
import { zen2han, scrollTop } from '../modules/util'

// 送り状番号のデフォルト値
const defaultValues = ['', '', '', '', '', '', '', '', '', '']
// エラーメッセージのデフォルト値
const defaultErrorMessages = ['', '', '', '', '', '', '', '', '', '']
// エラーメッセージのデフォルト値
const defaultExpands = [false, false, false, false, false, false, false, false, false, false]
// 入力値のノーマライズ関数
const normalize = (values: string[]) => {
  // console.log('normalize', values)
  let work = [...values]
  // 全角数値を半角にする
  work.forEach((value, index) => { work[index] = zen2han(value) })
  // 未入力を前詰め
  work = work.filter((value) => value)
  // 重複入力の除去
  const s = new Set<string>(work)
  work = [...(s as any)]
  // フォームへの適用
  const newValues = [...defaultValues]
  work.forEach((value, index) => newValues[index] = value)
  // console.log('normalize', newValues)
  return newValues
}
// テーブルヘッダのCSSスタイル
const tableHeaderStyle: SxProps = {
  color: '#333',
  textAlign: 'center'
}

const elementIdScroll = (elementId: string, isMobileSize: boolean) => {
  var element = document.getElementById(elementId)
  var headerOffset = isMobileSize ? 15 : 10
  if (element) {
    var elementPosition = element.getBoundingClientRect().top
    var offsetPosition = elementPosition + window.pageYOffset - headerOffset
    window.scrollTo({
         top: offsetPosition,
         behavior: "smooth"
    })
  }
}

/**
 * 検索ページ
 */
const SearchPage: React.FC = () => {
  const navigate = useNavigate()
  const linkParams = useParams<{ invoiceNoList: string}>()
  const noList = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
  const [formValues, setFormValues] = useState<string[]>([...defaultValues])
  const [warningMessage, setWarningMessage] = useState<string>('')
  const [errorMessages, setErrorMessages] = useState<string[]>([...defaultErrorMessages])
  const [expands, setExpands] = useState<boolean[]>([...defaultExpands])
  const [params, setParams] = useState<IRequest | undefined>()
  const query = useContactCargo(params)
  const rows = query.data?.data.body?._obj0
  const {friendlyErrorMessage, clear: clearErrorData} = useMcdApiErrorData()

  const theme = useTheme();
  // 画面サイズが'sm(600px)'以上の場合true
  const isMobileSize = !useMediaQuery(theme.breakpoints.up('sm'));
  const textPosition = isMobileSize ? 'start' : 'center'
  const historyFontSize = isMobileSize ? '0.8em' : '1em'

  // エラー情報のクリア
  const clearErrorInformation = () => {
    setWarningMessage('')
    clearErrorData()
  }
  // 入力値検証
  const validate = (values: string[]) => {
    // console.log('validate', values)
    let error = false
    const messages = [...defaultErrorMessages]
    setErrorMessages(messages)
    if (values.join().length === 0) { return }
    let work = ''
    values.forEach((value, index) => {
      if (!value) { return  }
      if (!value.match(/^[0-9０-９]+$/)) {
        messages[index] = '送り状番号として無効な文字が含まれています。'
        error = error || true
      }
      work += value
    })
    if (work === '') {
      error = true
      messages[0] = '送り状番号を入力して下さい。'
    }
    setErrorMessages(messages)
    return !error
  }
  // 検索
  const search = () => {
    // console.log('search')
    clearErrorInformation()
    setExpands([...defaultExpands])
    setParams(undefined)
    // 入力値検証
    if (!validate(formValues)) {
      setWarningMessage('入力に誤りがあります。入力内容を確認して下さい。')
      scrollTop()
      return
    }
    // 入力値のノーマライズ
    const values = normalize(formValues)
    setFormValues(values)
    // console.log({values})
    // 検索実行URLに遷移
    const list = values.filter((value) => value )
    navigate(`/search/${list.join(',')}`)
  }
  // リセット
  const reset = () => {
    // 入力値と検索APIパラメータのクリア
    clearErrorInformation()
    setFormValues([...defaultValues])
    setExpands([...defaultExpands])
    setParams(undefined)
    // 初期URLに遷移
    navigate('/search')
  }
  // アコーディオンの開閉
  const expandAccordion = (index: number, expand?: boolean) => {
    const work = [...expands]
    if (expand === true) {
      work[index] = true
    } else {
      work[index] = !work[index]
    }
    setExpands(work)
  }
  // 検索API実行
  useEffect(() => {
    if (!linkParams) { return }
    const list = linkParams.invoiceNoList?.split(',')
    if (list === undefined || list.length === 0) { return }
    // 空白を無視して配列を生成
    let linkValues = list.filter((value) => value)
    // 重複入力の除去
    const s = new Set<string>(linkValues)
    linkValues = [...(s as any)]
    const work: string[] = [...defaultValues]
    // パラメータで渡された送り状番号をフォームにセット
    linkValues
      .filter((value, index) => index < 10 ) // 10件で打ち切り
      .forEach((value, index) => work[index] = value )
    setFormValues(work)
    // 入力値検証
    if (!validate(work)) { return }
    // 検索APIパラメータセット
    let params: IRequest | undefined = undefined
    const values = normalize(work)
    setFormValues(values)
    if (values) {
      params = {}
      values.forEach((value, index) => {
        work[index] = value;
        (params as any)[`INVOICE_NO${index + 1}`] = value
      })
    }
    setParams(params)
  }, [linkParams])

  return (
    <Box>
      <PageHeader title='荷物問い合わせサービス' />

      <Grid container className='content-wrapper'>
        {/* 検索 */}
        <Grid item xs={12} md={8} lg={6} className='content'>
          {/* 送り状番号検索 */}
          <Box sx={{
            pb: 4,
            mt: 1,
            mb: 4,
            borderBottom: 'solid 1px rgba(0, 0, 0, 0.12)'
          }}>
            <Typography component='h2' sx={{fontSize:'1.2em', mb:1}}>送り状番号検索</Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6} md={5}>
                <Button variant='contained' size='large' fullWidth
                  onClick={() => { search() }}
                >お問い合わせ開始</Button>
              </Grid>
              <Grid item xs={12} sm={6} md={5}>
                <Button variant='outlined' size='large' fullWidth
                  onClick={() => { reset() }}
                >入力内容をクリア</Button>
              </Grid>
            </Grid>
          </Box>

          <ErrorInformation type='warning' message={warningMessage} />
          <ErrorInformation message={friendlyErrorMessage} />

          {/* 送り状番号入力：列タイトル */}
          <Grid container spacing={1}
            sx={{
              marginBottom: '10px'
            }}
          >
            <Grid item xs={8} sm={4} md={4} sx={tableHeaderStyle}>
              <Typography component='span'>送り状番号入力</Typography>
            </Grid>
            <Grid item xs={4} sm={3} md={3} sx={tableHeaderStyle}>
              <Typography component='span'>日付</Typography>
            </Grid>
            { isMobileSize ?
              <></> :
              <Grid item xs={6} sm={4} md={3} sx={tableHeaderStyle}>
                <Typography component='span'>配送状況</Typography>
              </Grid>
            }
          </Grid>
          {/* 送り状番号入力：フォーム */}
          {
            noList.map((no, index) => {
              const row = rows && rows[index]
              return (
                <Grid container spacing={1} key={index}
                  sx={{
                    alignItems: 'center',
                    mb: 1,
                  }}
                >
                {/* <React.Fragment key={index}> */}
                  <Grid item xs={8} sm={4} md={4}>
                    <TextField
                      fullWidth
                      size='small'
                      placeholder={`${no}件目の送り状番号`}
                      inputProps={{
                        inputMode: 'numeric',
                        maxLength: '20',
                      }}
                      value={formValues[index]}
                      onChange={(e) => {
                        formValues[index] = e.target.value
                        setFormValues([...formValues])
                      }}
                      error={!!errorMessages[index]}
                      helperText={errorMessages[index]}
                    />
                  </Grid>
                  <Grid item xs={4} sm={3} md={3} sx={{ textAlign:'center' }}>
                    <Typography>{row?.DELV_RESULT_DATE}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4} md={3} sx={{ textAlign:textPosition }}>
                    {
                      row?.INVOICE_NO &&
                        <a
                          tabIndex={-1}
                          onClick={() => {
                            const openFlg = expands[index]
                            if (!row?.NO_DATA_FLG ) {
                              // NO_DATA_FLGが「false」の場合、accordionを展開し検索結果へスクロール
                              expandAccordion(index, true)
                            } else {
                              // NO_DATA_FLGが「true」の場合、accordionを展開せず検索結果へスクロール
                              elementIdScroll(`detail-${row?.INVOICE_NO}`, isMobileSize)
                            }
                            if (openFlg) {
                              elementIdScroll(`detail-${row?.INVOICE_NO}`, isMobileSize)
                            }
                          }}
                        >
                          <MoveToDetailIcon sx={{verticalAlign:'middle', opacity:1}} />
                          <Typography component='span' sx={{fontWeight: 'bold'}}>
                            {!row?.NO_DATA_FLG ? row?.DELV_STATUS_NAME : '該当なし'}
                          </Typography>
                        </a>
                    }
                    </Grid>
                {/* </React.Fragment> */}
                </Grid>
              )
            })
          }
          {/* </Grid> */}
        </Grid>

        {/* 検索結果 */}
        {
          rows &&
            <Grid container className='content-wrapper'>
              <Grid item xs={12} md={8} lg={6}>
                <Typography component='h2' sx={{
                  my: 2
                }}>検索結果</Typography>
                <Box>
                  {
                    rows && rows.map((row, index) => (
                      (row.INVOICE_NO) &&
                        <React.Fragment key={index}>
                          <div id={`detail-${row.INVOICE_NO}`}></div>
                          <Accordion
                            TransitionProps= {
                              {
                                "timeout": 0,
                                // "onEnter": () => {console.log("onEnter")},
                                // "onEntering": () => {console.log("onEntering")},
                                "onEntered": () => {
                                  // console.log("onEntered")
                                  elementIdScroll(`detail-${row?.INVOICE_NO}`, isMobileSize)
                                },
                                // "onExit": () => {console.log("onExit")},
                                // "onExiting": () => {console.log("onExiting")},
                                // "onExited": () => {console.log("onExited")}
                              }
                            }
                            expanded={expands[index]}
                            onChange={() => {expandAccordion(index)}}
                            disabled={row.NO_DATA_FLG}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              sx={{
                                // NO_DATA_FLGが「true：白」、「false：灰色」
                                backgroundColor: !row.NO_DATA_FLG  ? '#fff' : '#eee',
                                opacity: '1 !important'
                              }}
                            >
                              <Grid container sx={{alignItems:'center'}}>
                                <Grid item xs={9} md={9}>
                                  <Label>
                                    <>
                                      送り状番号 {index + 1} 件目：
                                      <Typography component='span' sx={{color:'#000', fontSize:'1.5em', fontWeight:'bold'}}>{row.INVOICE_NO}</Typography>
                                    </>
                                  </Label>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                  <DisplayValue>
                                    {!row.NO_DATA_FLG ? row.DELV_STATUS_NAME : '該当なし'}
                                  </DisplayValue>
                                </Grid>
                                <Grid item xs={12} md={12}>
                                  {
                                    row.NO_DATA_FLG &&
                                      <Typography component='p' sx={{color:'red'}}>※送り状番号をご確認下さい。</Typography>
                                  }
                                </Grid>
                              </Grid>
                            </AccordionSummary>
                            <AccordionDetails>

                              <Box sx={{ my: 1 }}>
                                <Grid container spacing={1}
                                  sx={{
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                  }}
                                >
                                  <Grid item xs={12} sm={12} md={12}>
                                    <Box sx={{
                                      mb: 2,
                                      p: 2,
                                      backgroundColor: '#f5f5f5',
                                      borderRadius: '5px',
                                    }}>
                                      <Typography component='p' sx={{fontSize:'1.25rem', fontWeight:'bold'}}>{row.DELV_STATUS_NAME}</Typography>
                                      <Typography component='p'>{row.DELV_STATUS_ANN}</Typography>
                                    </Box>
                                </Grid>
                                  {
                                    // REDELIVERY_REQUEST_DISP_FLGがtrueの場合、ボタンを表示
                                    row.REDELIVERY_REQUEST_DISP_FLG &&
                                    <Grid item xs={12} sm={6} md={6}>
                                      <Button variant='contained' size='large' fullWidth
                                        disabled={
                                        // 配達完了または配達中止の場合
                                        row.DELV_STATUS === "50" || row.DELV_STATUS === "90" || row.NO_DATA_FLG || !row.CHG_REQ_BTN_ENABLE_FLG
                                        }
                                        onClick={() => { window.open(`/trace/request/${row.INVOICE_NO}`, '_blank') }}
                                      >お受取り日時を変更する</Button>
                                    </Grid>
                                  }
                                  <Grid item xs={12} md={12}>
                                    {
                                      // 配達完了または配達中止の場合
                                      row.REDELIVERY_REQUEST_DISP_FLG && (row.DELV_STATUS === "50" || row.DELV_STATUS === "90" || row.NO_DATA_FLG || !row.CHG_REQ_BTN_ENABLE_FLG) &&
                                        <Typography component='p' sx={{fontSize:'0.8rem', color:'red', textAlign:'center'}}>※この送り状番号ではご利用頂けません。</Typography>
                                    }
                                  </Grid>
                                </Grid>
                              </Box>

                              <Box sx={{ my: 2, p: 1 }}>
                                <Grid container spacing={1}>
                                  <Grid item xs={5} sm={4} md={3}><Label>お荷物個数：</Label></Grid>
                                  <Grid item xs={7} sm={8} md={9}>
                                    {
                                      row.BOX_QTY &&
                                      <DisplayValue>{row.BOX_QTY}</DisplayValue>
                                    }
                                  </Grid>
                                  <Grid item xs={5} sm={4} md={3}><Label>受付日時：</Label></Grid>
                                  <Grid item xs={7} sm={8} md={9}><DisplayValue>{row.PICKUP_CMPL_DATETIME}</DisplayValue></Grid>
                                  <Grid item xs={5} sm={4} md={3}><Label>受付営業所：</Label></Grid>
                                  <Grid item xs={7} sm={8} md={9}><DisplayValue>{row.PICKUP_DIVISION_NAME}</DisplayValue></Grid>
                                  <Grid item xs={5} sm={4} md={3}><Label>配達完了日時：</Label></Grid>
                                  <Grid item xs={7} sm={8} md={9}><DisplayValue>{row.DIST_CMPL_DATETIME}</DisplayValue></Grid>
                                  <Grid item xs={5} sm={4} md={3}><Label>配達営業所：</Label></Grid>
                                  <Grid item xs={7} sm={8} md={9}><DisplayValue>{row.DIST_DIVISION_NAME}</DisplayValue></Grid>
                                  <Grid item xs={5} sm={4} md={3}><Label>お問合せ先：</Label></Grid>
                                  <Grid item xs={7} md={9}>
                                    <DisplayValue>
                                      <>
                                        {row.CONTACT_NAME}<br />
                                        { row.CONTACT_PHONE_NO && <>TEL&nbsp;{row.CONTACT_PHONE_NO}&nbsp;&nbsp;<wbr /></> }
                                        { row.CONTACT_FAX_NO && <>FAX&nbsp;{row.CONTACT_FAX_NO}</> }
                                      </>
                                    </DisplayValue>

                                  </Grid>
                                </Grid>
                              </Box>

                              <Box
                                sx={{
                                  mx: 1,
                                  my: 2,
                                  px: 2,
                                  py: 4,
                                  backgroundColor: '#ece8ce',
                                  borderRadius: 2,
                                }}
                              >
                                {
                                  row.DELV_STATUS_HISTORY.map((history, index) => (
                                    <React.Fragment key={index}>
                                      {
                                        (index > 0) &&
                                          <Box sx={{textAlign: 'center', my: 1}}>
                                            <ArrowDownIcon sx={{ opacity: 0.33 }} />
                                          </Box>
                                      }
                                      <Grid container
                                        spacing={0.5}
                                        sx={{
                                          backgroundColor: '#fff',
                                          borderRadius: '20px',
                                          border: 'solid 1px rgba(0, 0, 0, 0.2)',
                                          p: 1,
                                          textAlign: 'center',
                                          alignItems: 'center',
                                          maxWidth: '500px',
                                          margin: 'auto'
                                        }}
                                      >
                                        <Grid item xs={4} md={4} sx={{lineHeight:'0.9em'}}>
                                          <Typography component='span' sx={{fontSize:historyFontSize, fontWeight:'bold'}}>{history.DELV_STATUS_NAME}</Typography>
                                        </Grid>
                                        <Grid item xs={4} md={4} sx={{lineHeight:'0.9em'}}>
                                          <Typography component='span' sx={{fontSize:historyFontSize}}>{history.RESULT_DATETIME}</Typography>
                                        </Grid>
                                        <Grid item xs={4} md={4} sx={{lineHeight:'0.9em'}}>
                                          <Typography component='span' sx={{fontSize:historyFontSize}}>{history.DIVISION_NAME}</Typography>
                                        </Grid>
                                      </Grid>
                                    </React.Fragment>
                                  ))
                                }
                              </Box>
                              <BackToPageTop />
                            </AccordionDetails>
                          </Accordion>
                        </React.Fragment>
                    ))
                  }
                </Box>
                <BackToPageTop />
              </Grid>
            </Grid>
        }

      </Grid>
      <HashLinkObserver />
    </Box>
  )
}

export default SearchPage
